import { useEffect, useState } from 'react';
import type { Session } from '../App';
import { Container, PageHeader } from '../App';

interface Props { session: Session; }

interface QuestView { id: string; display_name: string; category: string; repeatable: boolean; status: string; }

export default function Quests({ session }: Props) {
  const playerId = session.playerId;
  const [quests, setQuests] = useState<QuestView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const base = `/api/v1/games/${session.gameId}/modules/quest/quests/player/${playerId}`;

  const reload = () => {
    fetch(base)
      .then(r => r.json())
      .then(setQuests)
      .catch(e => setError(e.message));
  };
  useEffect(reload, [session.gameId, playerId]);

  const act = async (action: string, questId: string, body?: unknown) => {
    setBusy(true); setError(null);
    try {
      const opts: RequestInit = { method: 'POST' };
      if (body) { opts.headers = { 'Content-Type': 'application/json' }; opts.body = JSON.stringify(body); }
      const res = await fetch(`${base}/${action}/${questId}`, opts);
      if (!res.ok) throw new Error(await res.text());
      reload();
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  };

  const byStatus = (s: string) => quests.filter(q => q.status === s);

  return (
    <Container>
      <PageHeader title="Quests" subtitle="Accept, track, and complete quests for the selected player." />
      {error && <div className="error-banner">{error}</div>}

      {['active', 'available', 'completed', 'locked'].map(status => {
        const group = byStatus(status);
        if (group.length === 0) return null;
        return (
          <div key={status} className="mb-5">
            <h2 className="text-[13px] font-semibold uppercase tracking-[0.06em] text-fg-dim mb-2">{status} ({group.length})</h2>
            <div className="table-wrap">
              <table>
                <thead><tr><th>Quest</th><th>Category</th><th>Repeatable</th><th></th></tr></thead>
                <tbody>
                  {group.map(q => (
                    <tr key={q.id}>
                      <td className="font-medium">{q.display_name} <span className="text-xs text-fg-muted">({q.id})</span></td>
                      <td><span className="badge badge-muted">{q.category}</span></td>
                      <td>{q.repeatable ? 'Yes' : '—'}</td>
                      <td className="flex gap-2">
                        {status === 'available' && <button className="btn btn-primary" disabled={busy} onClick={() => act('accept', q.id)}>Accept</button>}
                        {status === 'active' && <button className="btn" disabled={busy} onClick={() => act('complete', q.id)}>Complete</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}

      {quests.length === 0 && (
        <div className="empty">
          <div className="text-fg-2 font-medium mb-1">No quests published</div>
          <div className="text-xs text-fg-muted">Create quests in Config Explorer → Quest, publish, then manage here.</div>
        </div>
      )}
    </Container>
  );
}
